
/**
 * @file dcp/src/dcp-client/worker/supervisor2/quanta.js
 *
 * A class to measure the difference in local worker perf vs jobPerfDatai.
 *
 * @author Paul, paul@distributive.network
 * @date December 2023,
 * @module quanta
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

/** @typedef {import('./index').Supervisor} Supervisor                   */

const { assert } = require('dcp/common/dcp-assert');
const wd = require('dcp/utils/worker-and-daemons');
const { selectiveDebug, selectiveDebug2 } = require('./common');

/**
 * SubQuanta.localTime := fixed-up EMA times series of CPUTime + GPUTime over all jobs.
 * SubQuanta.globalTime := fixed-up EMA times series of sliceCPUTime + sliceGPUTime over all jobs.
 * SubQuanta.defaultQuanta := fixed-up ratio: SubQuanta.localTime / SubQuanta.globalTime
 */
class SubQuanta
{
  #alpha;
  #minQuanta;
  #maxQuanta;
  #discreteIncrement;
  #discreteIncrementInverse;
  
  /**
   * @param {Supervisor} supervisor
   * @param {boolean}    isGPU
   */
  constructor(supervisor, isGPU)
  {
    this.supervisor = supervisor;
    this.isGPU = isGPU;
    // alpha=0.1818 gives an effective period of 10.0011
    // effectivePeriod := 2 / alpha - 1
    this.#alpha = 0.1818;
    // Prevent wild swings of this.defaultQuanta, which is roughly the ratio of
    //    local_worker_slice_time / jobPerfData_measured_slice_time.
    // We limit this ratio to be between 1/8th and 8.
    this.#minQuanta = 0.125;
    this.#maxQuanta = 8;
    // Because there will be slight differences between local_worker_slice_time and
    // jobPerfData_measured_slice_time even when the worker is the only worker hooked
    // up to the DCP scheduler, we prove a little rounding. The rounding is 1/32 buckets.
    this.#discreteIncrement = 0.03125;
    this.#discreteIncrementInverse = 32;
    // Adjust for GPU: corresponding ratio is limited to be between 1/64th and 64, and cut the
    // effective period to about 5.
    if (isGPU)
    {
      this.#alpha *= 2;
      this.#minQuanta /= 8;
      this.#maxQuanta *= 8;
    }

    // localTime := EMA times series of CPUTime (or GPUTime) over all jobs.
    this.localTime = 0;
    // globalTime := EMA times series of sliceCPUTime (or sliceGPUTime) over all jobs.
    this.globalTime = 0;
    // Fixed up localTime / globalTime.
    this.defaultQuanta = 1;
    // When averageLocalTime changes recompute.
    this.averageLocalTime = -1;
    // When averageGlobalTime changes recompute.
    this.averageGlobalTime = -1;
  }
  /**
   * Calculate EMA of this.localTime and this.globalTime .
   * Fix up this.globalTime if it exceeds thresholds.
   * Calculate this.defaultQuanta and fixup if it exceeds thresholds.
   * @param {number} averageLocalTime 
   * @param {number} averageGlobalTime 
   * @returns {number}
   */
  nextEMA (averageLocalTime, averageGlobalTime)
  {
    assert(averageLocalTime && averageGlobalTime);
    if (Math.abs(this.averageLocalTime - averageLocalTime) > 1e-3
      || Math.abs(this.averageGlobalTime - averageGlobalTime) > 1e-3)
    {
      selectiveDebug2() && console.debug(`jobQuanta: averageLocalTime=${averageLocalTime}/${this.averageLocalTime}, averageGlobalTime=${averageGlobalTime}/${this.averageGlobalTime}`);
      this.averageLocalTime = averageLocalTime;
      this.averageGlobalTime = averageGlobalTime;
      // Calculate EMA.
      this.localTime = wd.nextEma(this.localTime, averageLocalTime, this.#alpha);
      this.globalTime = wd.nextEma(this.globalTime, averageGlobalTime, this.#alpha);
      // Calculate ratio;
      this.defaultQuanta = this.localTime / this.globalTime;

      // Enforce reasonable cap and floor to keep things from getting too crazy.
      if (this.defaultQuanta < this.#minQuanta)
        this.globalTime = this.localTime / this.#minQuanta;
      else if (this.defaultQuanta > this.#maxQuanta)
        this.globalTime = this.localTime / this.#maxQuanta;
      this.defaultQuanta = this.localTime / this.globalTime

      // Discretize by discreteIncrement increments.
      this.defaultQuanta = (this.defaultQuanta > 1
        ? Math.floor(this.#discreteIncrementInverse * this.defaultQuanta)
        : Math.ceil(this.#discreteIncrementInverse * this.defaultQuanta)) * this.#discreteIncrement;
    }
    return this.defaultQuanta;
  }
  /**
   *  For a given job, the scheduler stores an EMA approximation of slice completion time.
   *  However, each worker also tracks the same information and the ratio of local-info to
   *  scheduler-info (viz., global-info) is returned by this.jobQuanta so we can tell the
   *  task distributor how much work to return from fetchTask so that the work actually takes
   *  5 minutes to complete when using all the worker sandboxes.
   * @returns {Object<string, number>}
   */
  calculateJobQuanta ()
  {
    /** @type {Object<string, number>} */
    const quanta = { 0: 1 };
    let averageLocalTime = 0, averageGlobalTime = 0;
    if (this.isGPU)
      for (const jobMan of this.supervisor.jobManagerInventory)
      {
        if (jobMan.emaGPUTime > 0 && jobMan.globalGPUTime > 0)
        {
          quanta[jobMan.jobId] = jobMan.emaGPUTime;
          averageLocalTime += jobMan.emaGPUTime;
          averageGlobalTime += jobMan.globalGPUTime;
          selectiveDebug2() && console.debug('jobQuanta: job state', this.supervisor.dbg.sliceSandboxStr, `l-density/g-density, ${jobMan.estimateGPUDensity}/${jobMan.metrics?.sliceGPUDensity}`, `local/global, ${jobMan.emaGPUTime}/${jobMan.globalGPUTime}`);
        }
      }
    else
      for (const jobMan of this.supervisor.jobManagerInventory)
      {
        if (jobMan.emaCPUTime > 0 && jobMan.globalCPUTime > 0)
        {
          quanta[jobMan.jobId] = jobMan.emaCPUTime;
          averageLocalTime += jobMan.emaCPUTime;
          averageGlobalTime += jobMan.globalCPUTime;
          selectiveDebug2() && console.debug('jobQuanta: job state', this.supervisor.dbg.sliceSandboxStr, `l-density/g-density, ${jobMan.estimateCPUDensity}/${jobMan.metrics?.sliceCPUDensity}`, `local/global, ${jobMan.emaCPUTime}/${jobMan.globalCPUTime}`);
        }
      }

    if (averageLocalTime && averageGlobalTime) // Fake jobId '0' to represent unknown jobs.
      quanta['0'] = this.nextEMA(averageLocalTime, averageGlobalTime);

    selectiveDebug2() && console.debug(`jobQuanta(${this.isGPU}): defaultQuanta ${quanta['0']}, localTime ${this.localTime}/${averageLocalTime}, globalTime ${this.globalTime}/${averageGlobalTime}, quanta:`, quanta);
    return quanta;
  }
}
class Quanta
{
  constructor (supervisor)
  {
    /** @type {SubQuanta} */
    this.CPU = new SubQuanta(supervisor, false /*isGPU*/);
    /** @type {SubQuanta} */
    this.GPU = new SubQuanta(supervisor, true /*isGPU*/);
  }
  calculateJobQuanta ()
  {
    return {
      CPU: this.CPU.calculateJobQuanta(),
      GPU: this.GPU.calculateJobQuanta()
    };
  }
}
exports.Quanta = Quanta;
